import React, { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
	Box,
	Display,
	Divider,
	Flex,
	Modal,
	ModalCloseButton,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	Spacer,
	useBreakpointValue,
	useDisclosure,
} from '@bonusly/design-system';
import { useFeatureFlag } from '@bonusly/who-am-i';

import { useBudget } from '../../hooks/use-budget';
import { AmountChip } from '../amount-chip';
import { FlashPointsChip } from '../flash-points-chip';
import { GiveModalConfirmDelete } from './give-modal-confirm-delete';
import { GiveModalContent, GiveModalContentHandle } from './give-modal-content';

interface GiveModalProps {
	content?: any;
	isOpen: boolean;
	onSuccess: VoidFunction;
	onClose: VoidFunction;
	via?: string;
}

export const GiveModal: FC<GiveModalProps> = ({ content, isOpen, onClose, onSuccess, via }) => {
	const { t } = useTranslation(['common', 'recognition']);
	const budget = useBudget();
	const monthlyAllowanceBalanceIsZero = budget.monthlyAllowanceBalance === 0;
	const monthlyAllowanceIsZero = budget.data.allowance === 0;
	const showAmountChip = !(monthlyAllowanceBalanceIsZero && monthlyAllowanceIsZero);
	const giveModalContentRef = useRef<GiveModalContentHandle>(null);
	const [hasVideoAttachment, setHasVideoAttachment] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const { isOpen: isConfirmModalOpen, onClose: onConfirmModalClose, onOpen: onConfirmDeleteVideoModalOpen } = useDisclosure();
	const isMobile = useBreakpointValue({ base: true, sm: false });
	const hasMobileVideoFeatureFlag = useFeatureFlag('mobile-video');
	const handleOnClose = () => {
		// If the user tries to close the modal after attaching a video,
		// we need to let them know doing so will delete the video.
		if (hasVideoAttachment) {
			onConfirmDeleteVideoModalOpen();
		} else {
			onClose();
		}
	};

	const handleConfirmDelete = () => {
		onConfirmModalClose();
		onClose();
	};

	const mobileAttributes = {
		height: '100vh',
		m: 0,
		sx: {
			'@supports (-webkit-touch-callout: none)': {
				height: '-webkit-fill-available',
				minHeight: '-webkit-fill-available',
				maxHeight: '-webkit-fill-available',
			},
		},
	};
	const desktopAttributes = {
		maxH: 'fit-content',
	};

	const mobileModalHeader = (
		<ModalHeader p={5}>
			<Flex alignItems="center" justifyContent="space-between">
				<ModalCloseButton position="unset" fontSize="md" fontWeight="medium" p={5} minW={16} bg="transparent" color="secondary.fg">
					{t('cancel', { ns: 'common' })}
				</ModalCloseButton>
				<Display textAlign="center" fontSize="lg" fontWeight="semibold">
					{t('bonus_input.mobile_modal_header', { ns: 'recognition' })}
				</Display>
				<Box minW={16} textAlign="right"></Box>
			</Flex>
		</ModalHeader>
	);
	const desktopModalHeader = (
		<ModalHeader>
			<Flex pr={7}>
				{t('bonus_input.modal_header', { ns: 'recognition' })}
				<Spacer />
				<FlashPointsChip />
				{showAmountChip && <AmountChip amount={budget.monthlyAllowanceBalance ?? 0} h={8} />}
				<ModalCloseButton borderRadius="full" top={4} />
			</Flex>
		</ModalHeader>
	);
	return (
		<>
			<Modal
				data-testid="give-modal"
				isOpen={isOpen}
				onClose={handleOnClose}
				scrollBehavior="outside"
				size="2xl"
				// Should we disable the overlay click all the time?
				closeOnOverlayClick={!isSubmitting}
			>
				<ModalOverlay
					// Temp Fix for Safari: Ensures modal overlay displays correctly when navigation bar is hidden
					{...(isMobile && hasMobileVideoFeatureFlag ? { bg: 'white' } : {})}
				/>
				<ModalContent {...(isMobile && hasMobileVideoFeatureFlag ? mobileAttributes : desktopAttributes)}>
					{isMobile && hasMobileVideoFeatureFlag ? mobileModalHeader : desktopModalHeader}
					<Divider />
					<GiveModalContent
						ref={giveModalContentRef}
						content={content}
						onSuccess={onSuccess}
						setIsSubmitting={setIsSubmitting}
						onVideoAttachmentChange={setHasVideoAttachment}
						via={via}
					/>
					{isSubmitting && <Box bgColor="whiteAlpha.700" rounded="md" cursor="not-allowed" inset={0} position="absolute" />}
				</ModalContent>
			</Modal>
			<GiveModalConfirmDelete isOpen={isConfirmModalOpen} onClose={onConfirmModalClose} onConfirm={handleConfirmDelete} />
		</>
	);
};
